/**
 * Vulnerability Modal State Management Hook
 *
 * Manages the open/close state for all modals in the vulnerabilities section
 * (close, note, tag, remove-tag, import, export, get-started, add-seed, and the
 * HackerOne submit modal).
 *
 * Extracting these booleans out of the large orchestrator component makes them
 * mockable in tests and keeps modal open-state in a single, testable place.
 *
 * This is a simple state management hook with no side effects or complex logic,
 * making it safe to extract and reuse.
 *
 * SCOPE — boolean open/close flags ONLY (intentional). This hook deliberately
 * owns *only* plain `boolean` modal-open flags. It EXCLUDES every confirm/
 * payload-carrying state — `confirmStatus`, the `*.confirmPayload` of
 * statusConfirm/severityConfirm/tagConfirm/untagConfirm, `pendingNote`, and
 * `keyboard.showDeleteModal` — because those carry data (the rows/text/action
 * being confirmed), not a bare open/closed bit. Each of those is owned by the
 * hook responsible for its payload (e.g. the bulk-confirmation hooks, the note
 * flow, the keyboard hook) over in `index.tsx`; folding them in here would
 * duplicate that ownership and strip their typed payloads down to a boolean.
 *
 * This is the ONE intentional divergence from the asset mirror,
 * `src/sections/asset/hooks/drawer/useAssetModalState.ts`, which DOES fold
 * `confirmStatus` and `bulkConfirmStatus` into the hook. Do NOT "fix" the
 * asymmetry by pulling the payload-carrying states in here — the divergence is
 * deliberate. (Note: all these excluded states must still be represented in
 * `anyVulnerabilityDialogOpen` in `index.tsx` so they gate the row-action
 * hotkeys; that aggregate — not this hook — is the single gating chokepoint.)
 *
 * Otherwise mirrors the pattern in
 * `src/sections/asset/hooks/drawer/useAssetModalState.ts`.
 */

import { useState } from 'react';

/**
 * Return type for useVulnerabilityModalState hook
 */
export interface VulnerabilityModalStateReturn {
  // Close (remediate) modal
  isCloseModalOpen: boolean;
  setIsCloseModalOpen: (open: boolean) => void;

  // Note modal
  isNoteModalOpen: boolean;
  setIsNoteModalOpen: (open: boolean) => void;

  // Tag modal
  isTagModalOpen: boolean;
  setIsTagModalOpen: (open: boolean) => void;

  // Remove tag modal
  isRemoveTagModalOpen: boolean;
  setIsRemoveTagModalOpen: (open: boolean) => void;

  // Import modal
  showImportModal: boolean;
  setShowImportModal: (open: boolean) => void;

  // Export modal
  showExportModal: boolean;
  setShowExportModal: (open: boolean) => void;

  // Get started modal (first-time user onboarding)
  isGetStartedModalOpen: boolean;
  setIsGetStartedModalOpen: (open: boolean) => void;

  // Add seed modal (opened from GetStartedModal seeds method)
  isAddSeedModalOpen: boolean;
  setIsAddSeedModalOpen: (open: boolean) => void;

  // HackerOne submit modal
  isHackerOneModalOpen: boolean;
  setIsHackerOneModalOpen: (open: boolean) => void;
}

/**
 * Hook to manage modal state for the vulnerabilities section.
 *
 * Provides state and setters for every modal whose open-state must gate the
 * table's row-action hotkeys (see `anyVulnerabilityDialogOpen` in
 * `sections/vulnerabilities/index.tsx`).
 *
 * @returns Object containing all modal states and their setters
 *
 * @example
 * ```tsx
 * const { isNoteModalOpen, setIsNoteModalOpen } = useVulnerabilityModalState();
 *
 * <Button onClick={() => setIsNoteModalOpen(true)}>Add Note</Button>
 * <NoteModal open={isNoteModalOpen} onClose={() => setIsNoteModalOpen(false)} />
 * ```
 */
export const useVulnerabilityModalState = (): VulnerabilityModalStateReturn => {
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const [isAddSeedModalOpen, setIsAddSeedModalOpen] = useState(false);
  const [isHackerOneModalOpen, setIsHackerOneModalOpen] = useState(false);

  return {
    isCloseModalOpen,
    setIsCloseModalOpen,
    isNoteModalOpen,
    setIsNoteModalOpen,
    isTagModalOpen,
    setIsTagModalOpen,
    isRemoveTagModalOpen,
    setIsRemoveTagModalOpen,
    showImportModal,
    setShowImportModal,
    showExportModal,
    setShowExportModal,
    isGetStartedModalOpen,
    setIsGetStartedModalOpen,
    isAddSeedModalOpen,
    setIsAddSeedModalOpen,
    isHackerOneModalOpen,
    setIsHackerOneModalOpen,
  };
};
